// The DFT module's structure tools: a structure file (CIF, POSCAR / CONTCAR, XSF) or an entry of a structure database
// read into a complete recipe with the cell chosen (standard primitive, standard conventional, as given), scaled, and
// for a 2D layer the vacuum set;
// the 3D view of a recipe's cell (before or after a run); and the recipe's atoms as an editable table.
import { ActionIcon, Alert, Anchor, Badge, Button, FileButton, Group, List, NumberInput, ScrollArea, SegmentedControl, Stack, Table, Text,
  TextInput, Tooltip } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { ErrorBox } from "../../components/Errors";
import { atomRows, recipeStructureView, STRUCTURE_TEXT_KEPT, structureRead, withAtoms, type AtomRow, type CellChoice,
  type DbSource, type DftMemory, type Recipe, type RecipeProtocol, type StructureInfo, type StructureOptions } from "../../lib/qe";
import { StructurePanel } from "../skfitter/LatticeViews";
import { DatabaseSearch } from "./DatabaseSearch";

const DEFAULT_OPTIONS: StructureOptions = { cell: "primitive", scale: 1, vacuum: null };

/** A text file's content (FileReader: also where File.text() is missing). */
function readText(f: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result ?? ""));
    r.onerror = () => reject(r.error ?? new Error(`could not read ${f.name}`));
    r.readAsText(f);
  });
}
type StructureFile = NonNullable<DftMemory["structure_file"]>;

/** "Structure file" / "Database": open a file or take an entry of a structure database, choose the cell; every change
 * reads the structure again into a complete recipe. */
export function StructureImport({ saved, onLoad, onError, mode = "file", projectId, protocol = null }: {
  saved: StructureFile | undefined;
  onLoad: (recipe: Recipe, file: StructureFile) => void;
  onError: (e: unknown) => void;
  mode?: "file" | "database";
  projectId?: string;
  protocol?: RecipeProtocol | null;     // the protocol and library the recipe is made with (default: the server's)
}) {
  const [file, setFile] = useState<{ name: string; text: string | null; source?: DbSource } | null>(
    saved ? { name: saved.name, text: saved.text, source: saved.source } : null);
  const [opts, setOpts] = useState<StructureOptions>(saved?.options ?? DEFAULT_OPTIONS);
  const [info, setInfo] = useState<StructureInfo | null>(saved?.info ?? null);
  const [busy, setBusy] = useState(false);
  const [debounced] = useDebouncedValue(opts, 500);
  const first = useRef(true);

  const read = (name: string, text: string, o: StructureOptions, source?: DbSource) => {
    setBusy(true);
    structureRead(text, name, o, "none", source?.label ?? "", protocol).then((r) => {
      setInfo(r.info);
      onError(null);
      onLoad(r.recipe, { name, text: text.length <= STRUCTURE_TEXT_KEPT ? text : null, options: o, info: r.info,
        ...(source ? { source } : {}) });
    }).catch(onError).finally(() => setBusy(false));
  };
  // a change of the cell, the scale or the vacuum: read the structure again (not on the first render of a saved one)
  useEffect(() => {
    if (first.current) { first.current = false; return; }
    if (file?.text) read(file.name, file.text, debounced, file.source);
  }, [debounced]);

  const take = (name: string, text: string, source?: DbSource) => {
    setFile({ name, text, source });
    const o = { ...DEFAULT_OPTIONS };
    setOpts(o);
    read(name, text, o, source);
  };
  const pick = async (f: File | null) => {
    if (!f) return;
    const text = await readText(f).catch((e) => { onError(e); return null; });
    if (text != null) take(f.name, text);
  };
  const canChange = !!file?.text;
  return (
    <Stack gap={6} data-testid="structure-import">
      {mode === "file" ? (
        <>
          <Text size="xs" c="dimmed">A crystal structure: CIF, POSCAR / CONTCAR (VASP 5, with element names) or XSF. The recipe is made
            complete for you - the cell you choose, the standard band path, this server's recommended pseudopotentials and cutoffs, and
            the k-mesh - and can then be changed like any other.</Text>
          <Group gap="xs">
            <FileButton onChange={pick}>
              {(props) => <Button size="xs" variant="default" {...props} loading={busy}>{file ? "Open another structure file…" : "Open a structure file…"}</Button>}
            </FileButton>
            {file && <Text size="xs" c="dimmed">{file.name}</Text>}
          </Group>
        </>
      ) : (
        <DatabaseSearch projectId={projectId ?? ""} busy={busy} onPick={take} onError={onError} />
      )}
      {file && info && (
        <>
          {file.source && (
            <Text size="xs" data-testid="structure-source">Structure: {file.source.url
              ? <Anchor href={file.source.url} target="_blank" rel="noreferrer" size="xs">{file.source.label}</Anchor>
              : file.source.label}</Text>
          )}
          <SegmentedControl size="xs" value={opts.cell} disabled={!canChange || busy} aria-label="Cell"
            onChange={(v) => setOpts((o) => ({ ...o, cell: v as CellChoice, vacuum: null }))}
            data={[{ value: "primitive", label: `Primitive (${info.atoms.primitive})` },
              { value: "conventional", label: `Conventional (${info.atoms.conventional})` },
              { value: "as_given", label: `${file.source ? "As in the database" : "As in the file"} (${info.atoms.file})` }]} />
          <Group gap="xs" align="flex-end">
            <NumberInput size="xs" w={130} label="Scale the lattice" value={opts.scale} step={0.005} min={0.5} max={2} decimalScale={4}
              disabled={!canChange || busy} onChange={(v) => typeof v === "number" && v >= 0.5 && v <= 2 && setOpts((o) => ({ ...o, scale: v }))} />
            {info.dimensionality === "2D" && (
              <NumberInput size="xs" w={190} label={`Cell along a${info.vacuum_axes[0] + 1} (Å, sets the vacuum)`} placeholder="as it is"
                value={opts.vacuum ?? ""} min={5} max={200} step={1} disabled={!canChange || busy}
                onChange={(v) => setOpts((o) => ({ ...o, vacuum: typeof v === "number" && v >= 5 ? v : null }))} />
            )}
          </Group>
          {!canChange && <Text size="xs" c="dimmed">The structure is too large to keep in the project: {file.source
            ? "take the entry again" : "open the file again"} to choose another cell.</Text>}
          <StructureSummary info={info} />
        </>
      )}
    </Stack>
  );
}

export function StructureSummary({ info }: { info: StructureInfo }) {
  const cell = { primitive: "standard primitive cell", conventional: "standard conventional cell", as_given: "the cell of the file" }[info.cell];
  return (
    <Stack gap={4} data-testid="structure-summary">
      <Group gap={6}>
        <Text size="sm" fw={600}>{info.formula}</Text>
        <Badge size="sm" variant="light">{info.spacegroup.symbol} ({info.spacegroup.number})</Badge>
        <Badge size="sm" variant="light" color={info.dimensionality === "3D" ? "blue" : "grape"}>{info.dimensionality}</Badge>
        <Text size="xs" c="dimmed">{info.format.toUpperCase()}</Text>
      </Group>
      <Text size="xs">{info.atoms.used} atoms in the {cell} · a, b, c = {info.abc.map((x) => x.toFixed(3)).join(", ")} Å
        · α, β, γ = {info.angles.map((x) => x.toFixed(2)).join(", ")}°
        {info.vacuum_gap != null && ` · ${info.vacuum_gap.toFixed(1)} Å of vacuum along a${info.vacuum_axes[0] + 1}`}</Text>
      <Text size="xs">Band path {info.path_labels.join(" – ")} · SCF k-mesh {info.kmesh.join(" × ")}</Text>
      {info.warnings.length > 0 && (
        <Alert color="orange" p="xs"><List size="xs">{info.warnings.map((w) => <List.Item key={w}>{w}</List.Item>)}</List></Alert>
      )}
      {info.notes.map((n) => <Text key={n} size="xs" c="dimmed">{n}</Text>)}
    </Stack>
  );
}

/** The recipe's cell in 3D (the server draws the view from the recipe; no calculation needed). */
export function RecipeStructure({ recipe, onPick, picked, hideInfo }: {
  recipe: Recipe; onPick?: (atom: number) => void; picked?: number | null; hideInfo?: boolean;
}) {
  const [json] = useDebouncedValue(JSON.stringify(recipe), 500);
  const q = useQuery({ queryKey: ["dft-structure", json], queryFn: () => recipeStructureView(JSON.parse(json)),
    enabled: !!json, placeholderData: (p) => p, retry: false });
  if (q.error && !q.data) return <ErrorBox error={q.error} />;
  if (!q.data) return <Text size="sm" c="dimmed">Drawing the structure …</Text>;
  return <StructurePanel sv={q.data} onPick={onPick} picked={picked} hideInfo={hideInfo} />;
}

/** The recipe's atoms: species and coordinates (in the recipe's units), add and remove. */
export function AtomsTable({ recipe, onChange }: { recipe: Recipe; onChange: (r: Recipe) => void }) {
  const rows = atomRows(recipe);
  const units = String(recipe.structure?.positions ?? "crystal");
  const set = (i: number, patch: Partial<AtomRow>) => onChange(withAtoms(recipe, rows.map((r, k) => (k === i ? { ...r, ...patch } : r))));
  const coord = (i: number, key: "x" | "y" | "z", v: number) => (
    <NumberInput size="xs" w={100} value={v} step={0.01} decimalScale={6} hideControls aria-label={`atom ${i + 1} ${key}`}
      onChange={(n) => typeof n === "number" && set(i, { [key]: n })} />
  );
  return (
    <Stack gap={4} data-testid="atoms-table">
      <Text size="xs" c="dimmed">Positions in {units === "crystal" ? "fractions of the lattice vectors" : units}. A new element gets a
        species of its own: choose its pseudopotential in the table above.</Text>
      <ScrollArea.Autosize mah={300}>
        <Table fz="xs" verticalSpacing={2} withRowBorders={false}>
          <Table.Thead><Table.Tr><Table.Th>#</Table.Th><Table.Th>Species</Table.Th><Table.Th>x</Table.Th><Table.Th>y</Table.Th>
            <Table.Th>z</Table.Th><Table.Th /></Table.Tr></Table.Thead>
          <Table.Tbody>
            {rows.map((r, i) => (
              <Table.Tr key={i}>
                <Table.Td>{i + 1}</Table.Td>
                <Table.Td><TextInput size="xs" w={70} key={`${i}-${r.label}`} defaultValue={r.label} aria-label={`atom ${i + 1} species`}
                  onBlur={(e) => {
                    const v = e.currentTarget.value.trim();
                    if (/^[A-Z][a-z]?[A-Za-z0-9_]{0,2}$/.test(v) && v !== r.label) set(i, { label: v });
                    else e.currentTarget.value = r.label;            // not a species name: back to what it was
                  }} /></Table.Td>
                <Table.Td>{coord(i, "x", r.x)}</Table.Td>
                <Table.Td>{coord(i, "y", r.y)}</Table.Td>
                <Table.Td>{coord(i, "z", r.z)}</Table.Td>
                <Table.Td>
                  <Tooltip label="Remove this atom"><ActionIcon size="sm" variant="subtle" color="red" disabled={rows.length < 2}
                    aria-label={`remove atom ${i + 1}`} onClick={() => onChange(withAtoms(recipe, rows.filter((_, k) => k !== i)))}>×</ActionIcon></Tooltip>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </ScrollArea.Autosize>
      <Group><Button size="compact-xs" variant="default"
        onClick={() => onChange(withAtoms(recipe, [...rows, { label: rows[rows.length - 1]?.label ?? "H", x: 0.5, y: 0.5, z: 0.5 }]))}>
        Add an atom</Button></Group>
    </Stack>
  );
}
